import React, { useEffect, useState } from "react";
import { Box, Grid, TextField, FormControlLabel, Switch } from "@mui/material";
import { useAuth, useLoading } from "../../Context/Contexts";
import { useTranslation } from "react-i18next";
import { SalesDocumentViewModel, Option } from "../../Types/ExportTypes";
import { CountryAutoComplete } from "../AutoComplete/AutoCompletes";
import AsyncAutoComplete from "../AsyncAutoComplete";
import ShippingAgentServiceSelect from "../Shipping/ShippingAgentServiceSelect";

interface Props {
  orderNo: string;
  showShipping?: boolean;
}

const OrderInfoHeader: React.FC<Props> = ({ orderNo, showShipping = false }) => {
    const { authService } = useAuth();
    const { showLoading, hideLoading, isLoading } = useLoading();
  const { t } = useTranslation();

  const [order, setOrder] = useState<SalesDocumentViewModel | null>(null);
  const [shippingAgents, setShippingAgents] = useState<Option[]>([]);
  const [shippingServices, setShippingServices] = useState<Option[]>([]);
  const [showMore, setShowMore] = useState(false);

  const fetchOrder = async () => {
    showLoading();
    try {
      const res = await authService.get<SalesDocumentViewModel>(`/orderApi/GetOrder/?documentNo=${orderNo}`);
      setOrder(res);
      return res;
    } finally {
      hideLoading();
    }
  };

  useEffect(() => { fetchOrder(); }, [orderNo]);

  const updateOrder = async (changes: Partial<SalesDocumentViewModel>) => {
    if (!order) return;
    const updated = { ...order, ...changes };
    setOrder(updated);
    showLoading();
    try {
      const res = await authService.post<SalesDocumentViewModel>("/orderApi/UpdateOrder", updated);
      setOrder(res);
    } finally {
      hideLoading();
    }
  };

  const fetchShippingAgentOptions = async (search: string, offset: number, code?: string): Promise<Option[]> => {
    const url = code
      ? `/autocompleteApi/ShippingAgent?code=${code}`
      : `/autocompleteApi/ShippingAgent?search=${search}&offset=${offset}&limit=50`;
    const response = await authService.get<Option[]>(url);
    return response || [];
  };

  const fetchShippingServiceOptions = async (
    search: string,
    offset: number,
    code?: string,
    shippingAgentCode?: string
  ): Promise<Option[]> => {
    const agent = shippingAgentCode ?? order?.shippingAgentCode;
    if (!agent && !shippingAgentCode) return [];
    if (code) {
      const response = await authService.get<Option[]>(
        `/autocompleteApi/ShippingService?shippingAgentCode=${agent}&code=${code}`
      );
      return response || [];
    }
    const response = await authService.get<Option[]>(
      `/autocompleteApi/ShippingService?shippingAgentCode=${agent}&search=${search}&offset=${offset}&limit=50`
    );
    return response || [];
  };

  useEffect(() => {
    if (!order || !showShipping) return;
    const init = async () => {
      const [agentList, serviceList] = await Promise.all([
        fetchShippingAgentOptions("", 0, order.shippingAgentCode),
        fetchShippingServiceOptions("", 0, order.shippingServiceCode, order.shippingAgentCode)
      ]);
      setShippingAgents(agentList);
      setShippingServices(serviceList);
    };
    init();
  }, [order, showShipping]);

  if (!order) return null;

    return (

    <Box sx={{ mb: 2, p: 1, backgroundColor: "secondary.main", borderRadius: 1 }}>
      <FormControlLabel
        control={<Switch checked={showMore} onChange={(e) => setShowMore(e.target.checked)} />}
        label={t("common.showMoreInfo")}
      />
      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <TextField
            label={t("common.shipToName")}
            value={order.shiptoName}
            onChange={(e) => updateOrder({ shiptoName: e.target.value })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={t("common.shipToAddress")}
            value={order.shiptoAddress}
            onChange={(e) => updateOrder({ shiptoAddress: e.target.value })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("common.shipToPostCode")}
            value={order.shiptoPostCode}
            onChange={(e) => updateOrder({ shiptoPostCode: e.target.value })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("common.shipToCity")}
            value={order.shiptoCity}
            onChange={(e) => updateOrder({ shiptoCity: e.target.value })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CountryAutoComplete
            key={order.shiptoCountryCode || null}
            valueId={order.shiptoCountryCode || null}
            onChange={(o) => updateOrder({ shiptoCountryCode: o?.value || "" })}
          />
        </Grid>
        {showShipping && (
          <>
            <Grid item xs={12} md={6}>
                <ShippingAgentServiceSelect
                  key={`${order?.shippingAgentCode ?? ""}::${order?.shippingServiceCode ?? ""}`}
                  shippingAgentCode={order.shippingAgentCode}
                  shippingServiceCode={order.shippingServiceCode}
                  onChange={(agent, service) =>
                    updateOrder({ shippingAgentCode: agent, shippingServiceCode: service })
                  }
                isLoading={isLoading}
                            />
                        </Grid>
          </>
        )}
        {showMore && (
          <>
            <Grid item xs={12} md={6}>
              <TextField label={t("common.orderNo")} value={order.no} fullWidth disabled />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={t("common.selltoCustomerNo")} value={order.selltoCustomerNo} fullWidth disabled />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("common.selltoCustomerName")}
                value={order.selltoCustomerName}
                onChange={(e) => updateOrder({ selltoCustomerName: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("common.selltoAddress")}
                value={order.selltoAddress}
                onChange={(e) => updateOrder({ selltoAddress: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("common.selltoPostCode")}
                value={order.selltoPostCode}
                onChange={(e) => updateOrder({ selltoPostCode: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("common.selltoCity")}
                value={order.selltoCity}
                onChange={(e) => updateOrder({ selltoCity: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("common.selltoCounty")}
                value={order.selltoCounty}
                onChange={(e) => updateOrder({ selltoCounty: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CountryAutoComplete
                                key={order.selltoCountryCode || null}
                                valueId={order.selltoCountryCode || null}
                                onChange={(o) => { } }
                                disabled={true}
              />
            </Grid>
          </>
        )}
      </Grid>
            </Box>
  
  );
};

export default OrderInfoHeader;
